import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useT } from "../../lib/use-t";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { ForbiddenPanel } from "../../components/admin/ForbiddenPanel";
import { UserDetailHeader } from "../../components/admin/UserDetailHeader";
import { SessionsList } from "../../components/admin/SessionsList";
import { TurnsTable } from "../../components/history/TurnsTable";
import { authedFetch } from "../../lib/api-client";
import type { AdminTrendPoint, AdminUserDetail } from "../../lib/admin/types";

type LoadState =
  | { status: "loading" }
  | { status: "ok"; detail: AdminUserDetail }
  | { status: "forbidden" }
  | { status: "not_found" }
  | { status: "error"; message: string };

export function AdminUserDetailPage() {
  const t = useT();
  const params = useParams<{ id: string }>();
  const [search, setSearch] = useSearchParams();
  const id = params.id ?? "";
  const turnsPage = Math.max(1, Number(search.get("turnsPage") ?? "1") || 1);

  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    if (!/^\d+$/.test(id)) {
      setState({ status: "not_found" });
      return;
    }
    setState({ status: "loading" });
    authedFetch(`/admin/users/${id}?turnsPage=${turnsPage}`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 403) {
          setState({ status: "forbidden" });
          return;
        }
        if (r.status === 404) {
          setState({ status: "not_found" });
          return;
        }
        const body = await r.json();
        if (!body.ok) {
          setState({
            status: "error",
            message: body.error?.message ?? `Server returned ${r.status}.`,
          });
          return;
        }
        setState({ status: "ok", detail: body.data as AdminUserDetail });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : t("chrome.common.unknownError"),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [id, turnsPage]);

  return (
    <AdminLayout>
      {state.status === "loading" ? (
        <p className="text-sm text-[var(--ink-faint)]">{t("admin.userDetail.loading")}</p>
      ) : state.status === "forbidden" ? (
        <ForbiddenPanel />
      ) : state.status === "not_found" ? (
        <div className="space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight">{t("admin.userDetail.notFoundTitle")}</h1>
          <p className="text-sm text-[var(--ink-muted)] font-serif-italic">
            {t("admin.userDetail.notFoundDescriptionFmt", { id })}
          </p>
        </div>
      ) : state.status === "error" ? (
        <p
          role="alert"
          className="rounded-md border border-[var(--fail)] bg-[var(--fail)]/10 px-3 py-2 text-sm text-[var(--fail)]"
        >
          {state.message}
        </p>
      ) : (
        <Body
          detail={state.detail}
          onTurnsPageChange={(next) => {
            const sp = new URLSearchParams(search);
            sp.set("turnsPage", String(next));
            setSearch(sp, { replace: false });
          }}
        />
      )}
    </AdminLayout>
  );
}

function Body({
  detail,
  onTurnsPageChange,
}: {
  detail: AdminUserDetail;
  onTurnsPageChange: (page: number) => void;
}) {
  const t = useT();
  // The KPI block expects UserKpis but the API doesn't ship them on
  // /admin/users/:id (it ships sessions, trend, turns); recompute the
  // four numbers from the sessions list. Same algorithm as the LXC's
  // computeKpis() in lib/admin/queries.ts.
  const kpis = useMemo(() => {
    const finished = detail.sessions.filter((r) => r.finishedAt !== null);
    const sessionCount = detail.sessions.length;
    if (finished.length === 0) {
      return {
        sessionCount,
        finishedCount: 0,
        averagePercentage: null,
        passRate: null,
        trendDelta: null,
      };
    }
    const pcts = finished
      .map((r) => r.percentage)
      .filter((p): p is number => p !== null);
    const averagePercentage =
      pcts.length > 0 ? pcts.reduce((s, p) => s + p, 0) / pcts.length : null;
    const passDenom = finished.filter((r) => r.passed !== null);
    const passes = passDenom.filter((r) => r.passed === true).length;
    const passRate = passDenom.length > 0 ? passes / passDenom.length : null;
    let trendDelta: number | null = null;
    if (finished.length >= 4) {
      const window = finished.slice(0, Math.min(8, finished.length));
      const half = Math.floor(window.length / 2);
      const recentPcts = window
        .slice(0, half)
        .map((r) => r.percentage)
        .filter((p): p is number => p !== null);
      const priorPcts = window
        .slice(half, half * 2)
        .map((r) => r.percentage)
        .filter((p): p is number => p !== null);
      if (recentPcts.length > 0 && priorPcts.length > 0) {
        const avgOf = (xs: number[]) => xs.reduce((s, p) => s + p, 0) / xs.length;
        trendDelta = avgOf(recentPcts) - avgOf(priorPcts);
      }
    }
    return {
      sessionCount,
      finishedCount: finished.length,
      averagePercentage,
      passRate,
      trendDelta,
    };
  }, [detail.sessions]);

  return (
    <div className="space-y-10">
      <UserDetailHeader
        displayName={detail.user.displayName}
        identifier={detail.user.identifier}
        createdAt={detail.user.createdAt}
        roles={detail.user.roles}
        kpis={kpis}
        userId={detail.user.id}
      />

      <section aria-labelledby="trend-heading" className="space-y-2">
        <h2
          id="trend-heading"
          className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
        >
          {t("admin.userDetail.trendHeading")}
        </h2>
        <UserTrendChart points={detail.trend} />
      </section>

      <section aria-labelledby="sessions" className="space-y-3">
        <h2
          id="sessions"
          className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
        >
          {t("admin.userDetail.sessionsHeading")}
        </h2>
        <SessionsList sessions={detail.sessions} />
      </section>

      <section
        aria-labelledby="turns"
        className="border-t border-[var(--border)] pt-8 space-y-3"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 id="turns" className="text-lg sm:text-xl font-semibold tracking-tight">
            {t("admin.userDetail.turnsHeading")}
          </h2>
          <span className="text-xs text-[var(--ink-muted)]">
            {t("admin.userDetail.turnsSubtext")}
          </span>
        </div>
        <TurnsTable
          page={detail.turns}
          onPageChange={onTurnsPageChange}
          defaultOpenLatest
        />
      </section>
    </div>
  );
}

function UserTrendChart({ points }: { points: AdminTrendPoint[] }) {
  const t = useT();
  const data = useMemo(
    () =>
      points
        .filter((p) => p.percentage !== null)
        .map((p, i) => ({
          idx: i + 1,
          pct: Math.round((p.percentage ?? 0) * 100),
          dateLabel: new Date(p.startedAt).toLocaleDateString("nl-NL", {
            day: "numeric",
            month: "short",
          }),
        })),
    [points],
  );
  const { yMin, yMax } = useMemo(() => {
    if (data.length === 0) return { yMin: 0, yMax: 100 };
    const values = data.map((d) => d.pct);
    const lo = Math.max(0, Math.floor(Math.min(...values) / 10) * 10 - 10);
    const hi = Math.min(100, Math.ceil(Math.max(...values) / 10) * 10 + 10);
    return { yMin: lo, yMax: hi === lo ? lo + 10 : hi };
  }, [data]);
  if (data.length < 2) {
    return (
      <p className="text-sm text-[var(--ink-muted)]">
        {data.length === 1
          ? t("admin.userDetail.trendSingleSession")
          : t("admin.userDetail.trendNeedsSessions")}
      </p>
    );
  }
  return (
    <div className="h-[180px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="dateLabel"
            stroke="var(--ink-faint)"
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[yMin, yMax]}
            stroke="var(--ink-faint)"
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            unit="%"
            width={36}
          />
          <Tooltip
            cursor={{ stroke: "var(--border)", strokeWidth: 1 }}
            contentStyle={{
              background: "var(--panel)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              color: "var(--ink)",
              fontSize: 12,
            }}
            formatter={(value) => [`${value}%`, "Score"]}
          />
          <Line
            type="monotone"
            dataKey="pct"
            stroke="var(--accent)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: "var(--ink)", stroke: "var(--accent)", strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
